import { personSchema } from "@/content/author";
import { HOME_SHARE_TITLE, HOME_SHARE_DESCRIPTION, socialImageMeta } from "@/lib/social-sharing";
import { lazy, Suspense, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import portrait from "@/assets/portrait.jpg";
import bookCover from "@/assets/book-cover.webp";
import bookCover2 from "@/assets/book-cover-2.webp";
import bookCover3 from "@/assets/book-cover-3.webp";
import newBookCover from "@/assets/book-cover-space.jpg";
import heroFallback from "@/assets/hero-feature.jpg";
import cardFallback from "@/assets/card-ai.jpg";

import cop31Logo from "@/assets/cop31-logo.png.asset.json";

import yalnizlikPodcast from "@/assets/yalnizlik-as-podcast.mp3.asset.json";
// ShareButtons is heavy (canvas Story generator) — lazy-load so it doesn't
// land on the LCP critical request chain.
import { getHomePosts } from "@/lib/posts.functions";
import { listApprovedThemes } from "@/lib/themes-public.functions";
import { useLocalizedPosts } from "@/lib/wp.hooks";
import { useT } from "@/lib/i18n";
import { resizeImg } from "@/lib/img";
import { hreflangLinks, homeHreflangLinks, TR_PUBLISHER_ID, trPublisherSchema } from "@/lib/seo";
import { detectEnHost } from "@/lib/en-host";
import { EnHome } from "@/components/en/EnHome";
import { TrHomeRedesign } from "@/components/tr-home/TrHomeRedesign";
import {
  personSchema as enPersonSchema,
  websiteSchema as enWebsiteSchema,
  breadcrumbSchema as enBreadcrumbSchema,
  enHreflangLinks,
  EN_HOME,
  TR_SITE_URL,
} from "@/lib/en-schema";
import { displaySlug } from "@/lib/slug-aliases";
import { homepageArticles } from "@/lib/articles-meta";
import { listPublishedMagazine } from "@/lib/magazine.functions";
import { getLatestEkonomimColumn } from "@/lib/ekonomim.functions";
import { talks, formatTalkDate } from "@/lib/talks";

import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";

const Newsletter = lazy(() =>
  import("@/components/Newsletter").then((m) => ({ default: m.Newsletter })),
);
const ShareButtons = lazy(() =>
  import("@/components/ShareButtons").then((m) => ({ default: m.ShareButtons })),
);

export const Route = createFileRoute("/")({
  head: ({ loaderData }) => {
    // smartsustainability.ai is the English site: its root URL serves the
    // English homepage, with English metadata and self-canonical to "/".
    if (loaderData?.isEnHost) {
      const TITLE = "Arda Öztaşkın — Essays on AI, judgment, and the climate era";
      const DESCRIPTION =
        "The English writing of Arda Öztaşkın: essays, original concepts, and frameworks on AI, sustainability, institutional trust, and human judgment.";
      return {
        meta: [
          { title: TITLE },
          { name: "description", content: DESCRIPTION },
          { property: "og:title", content: TITLE },
          { property: "og:description", content: DESCRIPTION },
          { property: "og:type", content: "website" },
          { property: "og:url", content: EN_HOME },
          { property: "og:locale", content: "en_US" },
          { property: "og:locale:alternate", content: "tr_TR" },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: TITLE },
          { name: "twitter:description", content: DESCRIPTION },
        ],
        links: [
          { rel: "canonical", href: EN_HOME },
          ...enHreflangLinks(EN_HOME, `${TR_SITE_URL}/`),
        ],
        scripts: [
          { type: "application/ld+json", children: JSON.stringify(enPersonSchema()) },
          { type: "application/ld+json", children: JSON.stringify(enWebsiteSchema()) },
          {
            type: "application/ld+json",
            children: JSON.stringify(enBreadcrumbSchema([{ name: "Home", item: EN_HOME }])),
          },
        ],
      };
    }
    // Must mirror the render-time filter in <Index/> so the preload targets the
    // actual LCP image. If this diverges, the browser preloads the wrong image
    // and the real LCP waits for JS discovery (adds ~1.4s resource load delay).
    const featuredPost = loaderData?.posts?.find((p) => p.slug !== "yalnizlik-ekonomisi");
    const src = featuredPost?.img;
    const lcp = src ? resizeImg(src, 1200, { ar: 3 / 2 }) : null;
    const lcpSrcset = src
      ? [600, 800, 1000, 1200, 1400].map((w) => `${resizeImg(src, w, { ar: 3 / 2 })} ${w}w`).join(", ")
      : null;
    return {
      meta: [
        { title: "Akıllı Sürdürülebilirlik — Arda Öztaşkın'ın yazıları" },
        {
          name: "description",
          content:
            "Arda Öztaşkın'ın yazdığı bağımsız Türkçe yayın: iklim, yapay zekâ, ekonomi ve insan deneyimi üzerine özgün yazılar, kavramlar ve kitaplar.",
        },
        { property: "og:title", content: HOME_SHARE_TITLE },
        {
          property: "og:description",
          content:
            HOME_SHARE_DESCRIPTION,
        },
        { property: "og:url", content: "https://akillisurdurulebilirlik.com/" },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: HOME_SHARE_TITLE },
        { name: "twitter:description", content: HOME_SHARE_DESCRIPTION },
        ...socialImageMeta(),
      ],
      links: [
        { rel: "canonical", href: "https://akillisurdurulebilirlik.com/" },
        ...homeHreflangLinks(),
        ...(lcp
          ? [
              {
                rel: "preload",
                as: "image",
                href: lcp,
                fetchPriority: "high",
                imageSrcSet: lcpSrcset ?? undefined,
                imageSizes: "(min-width: 768px) 58vw, 100vw",
              } as unknown as { rel: string; href: string },
            ]
          : []),

      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://akillisurdurulebilirlik.com/#website",
            name: "Akıllı Sürdürülebilirlik",
            url: "https://akillisurdurulebilirlik.com",
            publisher: { "@id": TR_PUBLISHER_ID },
            description:
              "Arda Öztaşkın'ın yazdığı bağımsız Türkçe yayın: iklim, yapay zekâ, ekonomi ve insan deneyimi üzerine özgün yazılar, kavramlar ve kitaplar.",
            inLanguage: "tr-TR",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://akillisurdurulebilirlik.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({ "@context": "https://schema.org", ...personSchema() }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(trPublisherSchema()),
        },
      ],
    };
  },
  loader: async () => {
    const isEnHost = await detectEnHost();
    // On the English domain the root renders the English homepage, which
    // doesn't need the Turkish post feed.
    if (isEnHost) return { posts: [], themes: [], isEnHost: true };
    const [posts, themes] = await Promise.all([getHomePosts(), listApprovedThemes()]);
    return { posts, themes, isEnHost: false };
  },
  component: Index,
});

function Index() {
  // The English domain renders a different homepage. Dispatch before any hook
  // runs so the two trees never share a hook order.
  const { isEnHost } = Route.useLoaderData();
  if (isEnHost) return <EnHome />;
  const { posts, themes } = Route.useLoaderData();
  return <TrHomeRedesign posts={posts} themes={themes} />;
}

function TrHome() {
  const { t, lang } = useT();
  const loaderData = Route.useLoaderData();
  const fetchHomePosts = useServerFn(getHomePosts);
  // The prerendered/edge-cached HTML can lag a fresh publish, so revalidate the
  // feed once after hydration — a newly published essay shows up immediately.
  const { data: livePosts } = useQuery({
    queryKey: ["home-posts"],
    queryFn: () => fetchHomePosts({ data: {} }),
    initialData: loaderData.posts,
    staleTime: 0,
  });
  const { articles, isTranslating } = useLocalizedPosts(livePosts ?? loaderData.posts, { scope: "home" });
  const filteredArticles = articles.filter((a) => a.slug !== "yalnizlik-ekonomisi");
  const featured = filteredArticles[0];
  const latest = filteredArticles.slice(1, 6);
  const staticPicks = homepageArticles(lang);
  const fetchMag = useServerFn(listPublishedMagazine);
  const [magEnabled, setMagEnabled] = useState(false);
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(() => setMagEnabled(true), { timeout: 3000 })
      : (setTimeout(() => setMagEnabled(true), 1500) as unknown as number);
    return () => {
      const cancel = (w as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (cancel) cancel(id);
      else clearTimeout(id as unknown as ReturnType<typeof setTimeout>);
    };
  }, []);
  const { data: dbMag } = useQuery({
    queryKey: ["magazine-homepage"],
    queryFn: () => fetchMag(),
    enabled: magEnabled,
  });
  const dbPicks = (dbMag ?? []).slice(0, 3).map((a) => ({
    slug: a.slug,
    title: a.title,
    excerpt: a.excerpt,
    magazine: a.magazine,
    date: a.display_date,
  }));
  const magazinePicks = (dbPicks.length > 0 ? dbPicks : staticPicks).slice(0, 3);

  const fetchEkonomim = useServerFn(getLatestEkonomimColumn);
  const { data: ekonomimLatest } = useQuery({
    queryKey: ["ekonomim-latest", lang],
    queryFn: () => fetchEkonomim({ data: { lang } }),
  });




  return (
    <div className="max-w-7xl mx-auto px-6">
      {/* Featured Essay */}
      {featured ? (
        <section className="py-12 md:py-20 border-b border-border">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <img
                src={featured.img ? resizeImg(featured.img, 1200, { ar: 3 / 2 }) : heroFallback}
                srcSet={
                  featured.img
                    ? [600, 800, 1000, 1200, 1400]
                        .map((w) => `${resizeImg(featured.img!, w, { ar: 3 / 2 })} ${w}w`)
                        .join(", ")
                    : undefined
                }
                sizes="(min-width: 768px) 58vw, 100vw"
                alt={featured.title}
                width={1280}
                height={832}
                fetchPriority="high"
                decoding="async"
                className="w-full aspect-[3/2] object-cover rounded-sm outline outline-1 -outline-offset-1 outline-ink/5"
              />
            </div>
            <div className="md:col-span-5 flex flex-col gap-6">
              <span className="text-clay font-medium text-xs uppercase tracking-[0.2em]">
                {featured.category}
              </span>
              <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-balance">
                {featured.title}
              </h1>
              <p className="text-base text-ink/80 leading-relaxed text-pretty">
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-4 mt-4">
                <div className="h-px flex-1 bg-ink/10" />
                <Link
                  to="/yazilar/$slug"
                  params={{ slug: displaySlug(featured.slug, lang) }}
                  className="text-xs uppercase tracking-[0.2em] font-bold border-b-2 border-clay pb-1 hover:text-clay transition-colors"
                >
                  {t("index.read_essay")}
                </Link>
                {featured.hasAudio && (
                  <Link
                    to="/yazilar/$slug"
                    params={{ slug: displaySlug(featured.slug, lang) }}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-clay border border-clay/40 rounded-full px-3 py-1.5 hover:bg-clay hover:text-paper transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                      <path d="M21 19a2 2 0 0 1-2 2h-1v-6h3zM3 19a2 2 0 0 0 2 2h1v-6H3z" />
                    </svg>
                    {lang === "tr" ? "Sesli dinle" : "Listen"}
                    {featured.audioDurationSeconds ? ` · ${Math.round(featured.audioDurationSeconds / 60)} ${lang === "tr" ? "dk" : "min"}` : ""}
                  </Link>
                )}
              </div>
              <Suspense fallback={null}>
                <ShareButtons slug={featured.slug} title={featured.title} img={featured.img ?? undefined} excerpt={featured.excerpt} category={featured.category} size="md" />
              </Suspense>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-16 md:py-24 border-b border-border">
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-balance">
            {t("index.hero_title")}
          </h1>
          <p className="mt-6 text-base text-ink/80 leading-relaxed text-pretty max-w-2xl">
            {t("index.hero_subtitle")}
          </p>
        </section>
      )}

      {/* Starter Path */}
      <section className="pt-10 md:pt-12">
        <Link
          to="/baslangic"
          className="group block border border-ink/15 hover:border-clay p-6 md:p-7 transition-colors"
        >
          <div className="flex items-start gap-5 md:gap-6">
            <span className="text-clay font-serif text-3xl md:text-4xl leading-none shrink-0">
              ✦
            </span>
            <div className="flex-1">
              <span className="text-[10px] uppercase tracking-[0.3em] text-clay font-medium">
                {lang === "tr" ? "Giriş Yolu" : "Starter Path"}
              </span>
              <h2 className="font-serif text-xl md:text-2xl leading-snug mt-2 text-balance group-hover:text-clay transition-colors">
                {lang === "tr"
                  ? "Buradan başla. Dört temalı okuma yolu."
                  : "Start here. Four themed reading journeys."}
              </h2>
              <p className="text-sm text-ink/70 leading-relaxed mt-2 max-w-2xl">
                {lang === "tr"
                  ? "Temeller, Sürdürülebilirlik Eleştirisi, Yapay Zekâ ve İnsan, Sistem Düşüncesi — hangi taraftan girmek istediğinize göre seçin."
                  : "Foundations, Sustainability Critique, AI & the Human, Systems Thinking — pick the door you want to enter through."}
              </p>
            </div>
            <span
              aria-hidden
              className="text-ink/40 group-hover:text-clay group-hover:translate-x-0.5 transition-all text-lg shrink-0"
            >
              →
            </span>
          </div>
        </Link>
      </section>

      {/* Latest Thinking */}
      <section className="py-16">

        <div className="flex justify-between items-end mb-12">
          <h2 className="font-serif text-3xl italic">
            {t("index.latest")}
            {isTranslating && (
              <span className="ml-3 align-middle inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-clay/15 border border-clay/40 text-[11px] uppercase tracking-[0.2em] text-clay not-italic font-sans font-medium">
                <span className="inline-block h-2 w-2 rounded-full bg-clay animate-pulse" />
                {t("common.translating")}
              </span>
            )}
          </h2>
          <Link
            to={lang === "tr" ? "/yazilar" : "/ideas"}
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink/25 text-[11px] uppercase tracking-[0.25em] text-ink hover:bg-ink hover:text-paper hover:border-ink transition-colors"
          >
            {t("index.view_archive")}
            <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-x-8 gap-y-16">
          {/* Ekonomim — Haftanın Yazısı (pinned, left) */}
          {ekonomimLatest && (
            <div className="group block">
              <a
                href={ekonomimLatest.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                {/* Fixed typographic cover — matches other cards' aspect-square image */}
                <div className="relative w-full aspect-square mb-6 overflow-hidden bg-ink text-paper flex items-center justify-center p-6">
                  <div className="font-serif italic text-paper leading-[0.95] text-[clamp(1.75rem,4.5vw,2.75rem)] tracking-tight text-center">
                    Ekonomim'de<br />
                    <span className="text-clay">Haftanın</span><br />
                    Yazısı
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-clay mb-3 block">
                  {lang === "tr" ? "Köşe · Ekonomim" : "Column · Ekonomim"}
                  {ekonomimLatest.date ? ` · ${ekonomimLatest.date}` : ""}
                </span>
                <h3 className="font-serif text-2xl leading-snug mb-3 group-hover:text-clay transition-colors text-balance">
                  {ekonomimLatest.title}
                </h3>
                {ekonomimLatest.excerpt && (
                  <p className="text-sm text-ink/70 leading-relaxed">{ekonomimLatest.excerpt}</p>
                )}
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <span className="inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-ink/20 group-hover:border-clay pb-0.5">
                    {lang === "tr" ? "Ekonomim'de Oku ↗" : "Read on Ekonomim ↗"}
                  </span>
                </div>
              </a>
              <Suspense fallback={null}>
                <ShareButtons
                  absoluteUrl={ekonomimLatest.url}
                  title={ekonomimLatest.title}
                  excerpt={ekonomimLatest.excerpt ?? undefined}
                  category={lang === "tr" ? "Köşe · Ekonomim" : "Column · Ekonomim"}
                  lang={lang === "en" ? "en" : "tr"}
                  className="mt-4"
                />
              </Suspense>

            </div>
          )}

          {latest[0] && (
            <div key={latest[0].slug} className="group block">
              <Link to="/yazilar/$slug" params={{ slug: displaySlug(latest[0].slug, lang) }} className="block">
                <img
                  src={latest[0].img ? resizeImg(latest[0].img, 480, { ar: 1 }) : cardFallback}
                  srcSet={
                    latest[0].img
                      ? [320, 400, 500, 640, 800].map((w) => `${resizeImg(latest[0].img!, w, { ar: 1 })} ${w}w`).join(", ")
                      : undefined
                  }
                  sizes="(min-width: 1280px) 400px, (min-width: 768px) 33vw, 100vw"
                  alt={latest[0].title}
                  loading="lazy" decoding="async"
                  width={1152}
                  height={1152}
                  className="w-full aspect-square object-cover mb-6 grayscale group-hover:grayscale-0 transition-[filter] will-change-[filter] duration-700"
                />
                <span className="text-[10px] uppercase tracking-[0.25em] text-clay mb-3 block">
                  {latest[0].category}
                </span>
                <h3 className="font-serif text-2xl leading-snug mb-3 group-hover:text-clay transition-colors text-balance">
                  {latest[0].title}
                </h3>
                <p className="text-sm text-ink/70 leading-relaxed">{latest[0].excerpt}</p>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <span className="inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-ink/20 group-hover:border-clay pb-0.5">
                    {lang === "tr" ? "Oku →" : "Read →"}
                  </span>
                  {latest[0].hasAudio && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-clay border border-clay/40 rounded-full px-2.5 py-1 group-hover:bg-clay group-hover:text-paper transition-colors">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                        <path d="M21 19a2 2 0 0 1-2 2h-1v-6h3zM3 19a2 2 0 0 0 2 2h1v-6H3z" />
                      </svg>
                      {lang === "tr" ? "Sesli dinle" : "Listen"}
                      {latest[0].audioDurationSeconds ? ` · ${Math.round(latest[0].audioDurationSeconds / 60)} ${lang === "tr" ? "dk" : "min"}` : ""}
                    </span>
                  )}
                </div>
              </Link>
              <Suspense fallback={null}>
                <ShareButtons slug={latest[0].slug} title={latest[0].title} img={latest[0].img ?? undefined} excerpt={latest[0].excerpt} category={latest[0].category} className="mt-4" />
              </Suspense>
            </div>
          )}

          {/* COP31 Hub Card — editorial frame in site palette (paper/ink/clay) */}
          <Link
            to="/cop31"
            className="group relative block"
          >
            {/* Double-rule editorial frame */}
            <div className="relative border border-ink/25 p-4 sm:p-5 bg-paper transition-colors group-hover:border-ink/50">
              <div className="absolute inset-1.5 border border-ink/15 pointer-events-none" aria-hidden="true" />

              {/* Tiny clay ornament + kicker */}
              <div className="relative flex items-center gap-2 mb-4">
                <span className="h-px w-4 bg-clay" aria-hidden="true" />
                <span className="text-[9px] uppercase tracking-[0.35em] text-clay font-semibold">
                  {lang === "tr" ? "Rehber" : "Guide"}
                </span>
              </div>

              {/* Cover: official COP31 Türkiye logo, used large */}
              <div className="relative w-full aspect-square mb-6 border border-ink/10 overflow-hidden bg-paper flex items-center justify-center p-1 sm:p-2">
                <img
                  src={cop31Logo.url}
                  alt="COP31 Türkiye Antalya logosu"
                  loading="lazy"
                  decoding="async"
                  width={480}
                  height={270}
                  className="w-full h-auto scale-[1.12] object-contain transition-transform duration-700 group-hover:scale-[1.16] dark:brightness-0 dark:invert"
                />
              </div>




              <span className="text-[10px] uppercase tracking-[0.25em] text-ink/60 mb-3 block">
                COP31 · Antalya · 9–20 Kasım 2026
              </span>
              <h3 className="font-serif text-2xl leading-snug mb-3 text-ink group-hover:text-clay transition-colors text-balance">
                {lang === "tr" ? "COP31'i Okumak" : "Reading COP31"}
              </h3>
              <p className="text-sm text-ink/75 leading-relaxed">
                {lang === "tr"
                  ? "Hedeflerden uygulamaya, vaatlerden finansmana, çevreden ekonomik rekabete: COP31'i okumak."
                  : "From targets to implementation, from pledges to finance, from environment to economic competition: reading COP31."}
              </p>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <span className="inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-ink/20 group-hover:border-clay group-hover:text-clay transition-colors pb-0.5">
                  {lang === "tr" ? "İncele →" : "Explore →"}
                </span>
              </div>
            </div>
          </Link>



          {latest.slice(1, 4).map((p) => (
            <div key={p.slug} className="group block">
              <Link to="/yazilar/$slug" params={{ slug: displaySlug(p.slug, lang) }} className="block">
                <img
                  src={p.img ? resizeImg(p.img, 480, { ar: 1 }) : cardFallback}
                  srcSet={
                    p.img
                      ? [320, 400, 500, 640, 800].map((w) => `${resizeImg(p.img!, w, { ar: 1 })} ${w}w`).join(", ")
                      : undefined
                  }
                  sizes="(min-width: 1280px) 400px, (min-width: 768px) 33vw, 100vw"
                  alt={p.title}
                  loading="lazy" decoding="async"
                  width={1152}
                  height={1152}
                  className="w-full aspect-square object-cover mb-6 grayscale group-hover:grayscale-0 transition-[filter] will-change-[filter] duration-700"
                />
                <span className="text-[10px] uppercase tracking-[0.25em] text-clay mb-3 block">
                  {p.category}
                </span>
                <h3 className="font-serif text-2xl leading-snug mb-3 group-hover:text-clay transition-colors text-balance">
                  {p.title}
                </h3>
                <p className="text-sm text-ink/70 leading-relaxed">{p.excerpt}</p>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <span className="inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-ink/20 group-hover:border-clay pb-0.5">
                    {lang === "tr" ? "Oku →" : "Read →"}
                  </span>
                  {p.hasAudio && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-clay border border-clay/40 rounded-full px-2.5 py-1 group-hover:bg-clay group-hover:text-paper transition-colors">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                        <path d="M21 19a2 2 0 0 1-2 2h-1v-6h3zM3 19a2 2 0 0 0 2 2h1v-6H3z" />
                      </svg>
                      {lang === "tr" ? "Sesli dinle" : "Listen"}
                      {p.audioDurationSeconds ? ` · ${Math.round(p.audioDurationSeconds / 60)} ${lang === "tr" ? "dk" : "min"}` : ""}
                    </span>
                  )}
                </div>
              </Link>
              <Suspense fallback={null}>
                <ShareButtons slug={p.slug} title={p.title} img={p.img ?? undefined} excerpt={p.excerpt} category={p.category} className="mt-4" />
              </Suspense>
            </div>
          ))}
        </div>
      </section>

      {/* Bio + Newsletter */}
      <section className="py-20 grid md:grid-cols-2 gap-16 border-t border-border">
        <div>
          <div className="flex items-center gap-4 mb-6">
            <img
              src={portrait}
              alt={t("common.author_portrait")}
              loading="lazy" decoding="async"
              width={512}
              height={512}
              className="size-16 rounded-full object-cover grayscale"
            />
            <div>
              <p className="font-serif text-xl italic leading-none">Arda Öztaşkın</p>
              <p className="text-xs uppercase tracking-[0.2em] text-ink/70 mt-1">
                {t("index.bio.role")}
              </p>
            </div>
          </div>
          <p className="text-sm text-ink/70 leading-relaxed max-w-sm">
            {t("index.bio.body")}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6">
            <Link
              to="/yazar"
              className="inline-block text-xs uppercase tracking-[0.2em] font-bold border-b border-ink/20 hover:border-clay pb-1"
            >
              {t("index.bio.more")}
            </Link>
            <a
              href="https://www.linkedin.com/in/ardaoztaskin/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold border-b border-ink/20 hover:border-clay pb-1"
            >
              LinkedIn
              <span aria-hidden>↗</span>
            </a>
            <a
              href="https://www.amazon.com/stores/Arda-%C3%96zta%C5%9Fk%C4%B1n/author/B0H596GS9Q?ref=ap_rdr&shoppingPortalEnabled=true&ccs_id=196e27b1-ce0b-4223-b58e-55cf1274311d"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold border-b border-ink/20 hover:border-clay pb-1"
            >
              Amazon Author
              <span aria-hidden>↗</span>
            </a>
          </div>
        </div>

        <Suspense fallback={null}>
          <Newsletter />
        </Suspense>
      </section>


      {/* Latest Magazine Articles */}
      <section className="pb-16 pt-12 border-t border-border">
        <h2 className="font-serif text-3xl italic mb-12">
          {lang === "tr" ? "Son Dergi Yazıları" : "Latest Magazine Articles"}
        </h2>
        <div className="grid md:grid-cols-3 gap-x-8 gap-y-12">
          {magazinePicks.map((a) => (
            <Link
              key={a.slug}
              to="/dergi/$slug"
              params={{ slug: a.slug }}
              className="group block border-t border-ink/15 pt-6"
            >
              <span className="text-[10px] uppercase tracking-[0.25em] text-clay block mb-3">
                {a.magazine}
              </span>
              <h3 className="font-serif text-xl md:text-2xl leading-snug mb-3 group-hover:text-clay transition-colors text-balance">
                {a.title}
              </h3>
              <p className="text-sm text-ink/70 leading-relaxed">{a.excerpt}</p>
              <span className="mt-4 inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-ink/20 group-hover:border-clay pb-0.5">
                {lang === "tr" ? "Oku →" : "Read →"}
              </span>
            </Link>
          ))}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          <Link
            to={lang === "tr" ? "/sozluk" : "/concepts"}
            className="group block bg-ink text-paper p-6 md:p-7 flex flex-col"
          >
            <span className="text-[10px] uppercase tracking-[0.25em] text-clay block mb-3">
              {lang === "tr" ? "Kavram Sözlüğü" : "Concept Lexicon"}
            </span>
            <h3 className="font-serif text-xl md:text-2xl italic leading-snug mb-3 text-balance">
              {lang === "tr"
                ? "Kırılganlık ekonomisi, epistemik silahlanma, temporal arbitraj…"
                : "Fragility economy, epistemic armament, temporal arbitrage…"}
            </h3>
            <p className="text-sm text-paper/70 leading-relaxed">
              {lang === "tr"
                ? "Yazılarda geçen özgün kavramların kısa tanımları."
                : "Short definitions of the original concepts that run through the essays."}
            </p>
            <span className="mt-auto pt-4 inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-paper/40 group-hover:border-clay pb-0.5 self-start">
              {lang === "tr" ? "Sözlüğe Git →" : "Open Lexicon →"}
            </span>
          </Link>
          <Link
            to={lang === "tr" ? "/raporlar" : "/research"}
            className="group block bg-clay text-paper p-6 md:p-7 flex flex-col"
          >
            <span className="text-[10px] uppercase tracking-[0.25em] text-paper/90 block mb-3">
              {lang === "tr" ? "Rapor Kütüphanesi" : "Report Library"}
            </span>
            <h3 className="font-serif text-xl md:text-2xl italic leading-snug mb-3 text-balance">
              {lang === "tr"
                ? "IPCC, IEA, WEF, Stanford AI Index, UNEP, OECD ve daha fazlası."
                : "IPCC, IEA, WEF, Stanford AI Index, UNEP, OECD and more."}
            </h3>
            <p className="text-sm text-paper/85 leading-relaxed">
              {lang === "tr"
                ? "Yazıların dayandığı saygın küresel rapor, endeks ve araştırmaların seçilmiş referans kütüphanesi."
                : "A curated reference library of reputable global reports, indices and research underpinning the essays."}
            </p>
            <span className="mt-auto pt-4 inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-paper/50 group-hover:border-paper pb-0.5 self-start">
              {lang === "tr" ? "Kütüphaneye Git →" : "Open Library →"}
            </span>
          </Link>
          <Link
            to="/konusmalar"
            className="group block bg-moss text-paper p-6 md:p-7 flex flex-col"
          >
            <span className="text-[10px] uppercase tracking-[0.25em] text-paper/90 block mb-3">
              {lang === "tr" ? "Konuşmalar" : "Talks"}
            </span>
            <h3 className="font-serif text-xl md:text-2xl italic leading-snug mb-3 text-balance">
              {lang === "tr"
                ? "Konferans, panel ve söyleşi kayıtları."
                : "Conference, panel and interview recordings."}
            </h3>
            <p className="text-sm text-paper/85 leading-relaxed">
              {lang === "tr"
                ? "Yapay zekâ, sürdürülebilirlik, iklim ve finans üzerine konuşmalar — en yeniden eskiye."
                : "Talks on AI, sustainability, climate and finance — newest first."}
            </p>
            <span className="mt-auto pt-4 inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-paper/50 group-hover:border-paper pb-0.5 self-start">
              {lang === "tr" ? "Konuşmalara Git →" : "Open Talks →"}
            </span>
          </Link>
          <Link
            to="/podcast"
            className="group block bg-ink text-paper p-6 md:p-7 flex flex-col"
          >
            <span className="text-[10px] uppercase tracking-[0.25em] text-paper/90 block mb-3 inline-flex items-center gap-2">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                <path d="M21 19a2 2 0 0 1-2 2h-1v-6h3zM3 19a2 2 0 0 0 2 2h1v-6H3z" />
              </svg>
              {lang === "tr" ? "Podcast" : "Podcast"}
            </span>
            <h3 className="font-serif text-xl md:text-2xl italic leading-snug mb-3 text-balance">
              {lang === "tr"
                ? "Yazıları sesli dinleyin — Spotify ve daha fazlası."
                : "Listen to the essays — on Spotify and more."}
            </h3>
            <p className="text-sm text-paper/85 leading-relaxed">
              {lang === "tr"
                ? "Akıllı Sürdürülebilirlik podcast'i: uzun-form yazıların seslendirilmiş bölümleri."
                : "The Akıllı Sürdürülebilirlik podcast: narrated editions of the long-form essays."}
            </p>
            <span className="mt-auto pt-4 inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-paper/50 group-hover:border-paper pb-0.5 self-start">
              {lang === "tr" ? "Podcast'i Dinle →" : "Open Podcast →"}
            </span>
          </Link>
        </div>
      </section>


      {/* Latest Talk */}
      {talks[0] && (
        <section className="pb-16 pt-12 border-t border-border">
          <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
            <h2 className="font-serif text-2xl italic">
              {lang === "tr" ? "En Son Söyleşi" : "Latest Talk"}
            </h2>
            <Link
              to="/konusmalar"
              className="text-[10px] uppercase tracking-[0.25em] font-bold border-b border-ink/20 hover:border-clay pb-0.5"
            >
              {lang === "tr" ? "Tüm Konuşmalar →" : "All Talks →"}
            </Link>
          </div>
          <div className="grid md:grid-cols-12 gap-5 md:gap-6 items-center max-w-3xl">
            <a
              href={talks[0].url}
              target="_blank"
              rel="noopener noreferrer"
              className="md:col-span-5 group relative block aspect-video w-full overflow-hidden bg-ink/10"
              aria-label={`YouTube'da aç: ${talks[0].topic}`}
            >
              <img
                src={`https://i.ytimg.com/vi/${talks[0].id}/hqdefault.jpg`}
                alt={talks[0].topic}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <span aria-hidden className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25">
                <span className="flex h-12 w-16 items-center justify-center rounded-lg bg-black/80 text-paper shadow-lg transition-transform group-hover:scale-105">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </span>
            </a>
            <div className="md:col-span-7 flex flex-col">
              <span className="text-[10px] uppercase tracking-[0.25em] text-clay font-medium">
                {formatTalkDate(talks[0].date, lang)}
              </span>
              <Link
                to="/konusmalar/$id"
                params={{ id: talks[0].id }}
                className="font-serif text-xl md:text-2xl leading-snug mt-2 text-balance hover:text-clay transition-colors"
              >
                {talks[0].topic}
              </Link>
              <p className="mt-1.5 text-sm text-ink/70">{talks[0].program}</p>
              <Link
                to="/konusmalar/$id"
                params={{ id: talks[0].id }}
                className="mt-3 inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-ink/20 hover:border-clay pb-0.5 self-start"
              >
                {lang === "tr" ? "İzle & Transkripti Oku →" : "Watch & Read Transcript →"}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Books */}
      <section className="pb-24 pt-12 border-t border-border">
        <h2 className="font-serif text-3xl italic mb-12">{t("index.books.heading")}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              cover: newBookCover,
              alt: "Hayatı Yaşanır Kılan, Boşluktur — Arda Öztaşkın kitap kapağı",
              badge: "Yeni · 2026",
              title: (
                <>
                  Hayatı Yaşanır Kılan, <span className="italic">Boşluktur</span>
                </>
              ),
              body:
                "Her şeyi verimlilik hesabına bağlayan çağda; güveni, muhakemeyi, anlamı ve nefes alacak boşluğu savunan yeni kitap.",
              cta: "Yeni kitabı incele",
              href: "/kitaplar/hayati-yasanir-kilan-bosluktur",
              external: false,
              highlight: true,
              podcast: false,
            },
            {
              cover: bookCover3,
              alt: "Yalnızlık A.Ş. — Arda Öztaşkın kitap kapağı",
              badge: "Yeni · 2026",
              title: (
                <>
                  Yalnızlık <span className="italic">A.Ş.</span>
                </>
              ),
              body:
                "Yalnızlık nasıl en kârlı sektör haline geldi? Yeni kitabın tam PDF'ini üyelere özel olarak ücretsiz indirin — indirmek için önce siteye üye olmanız yeterli.",
              cta: "Detaylar & PDF indir",
              href: "/kitaplar/yalnizlik-as",
              external: false,
              highlight: false,
              podcast: true,
            },
            {
              cover: bookCover,
              alt: "Yapay Zekâ: Gücün İki Yüzü — Arda Öztaşkın kitap kapağı",
              badge: t("index.book.badge"),
              title: (
                <>
                  {t("index.book.title_a")} <span className="italic">{t("index.book.title_b")}</span>
                </>
              ),
              body: t("index.book.body"),
              cta: t("index.book.cta"),
              href: "/kitaplar/yapay-zeka-gucun-iki-yuzu",
              external: false,
              highlight: false,
              podcast: false,
            },
            {
              cover: bookCover2,
              alt: "İklim Değil, İnsanlık Krizi — Arda Öztaşkın kitap kapağı",
              badge: t("index.book2.badge"),
              title: <span className="italic">{t("index.book2.title")}</span>,
              body: t("index.book2.body"),
              cta: t("index.book2.cta"),
              href: "/kitaplar/iklim-degil-insanlik-krizi",
              external: false,
              highlight: false,
              podcast: false,
            },
          ].map((b, i) => {
            const ctaInner = (
              <>
                <span className="text-xs uppercase tracking-[0.2em] font-bold border-b border-ink/20 group-hover:border-clay pb-1">
                  {b.cta}
                </span>
                <span className="text-clay group-hover:translate-x-1 transition-transform">
                  {b.external ? "↗" : "→"}
                </span>
              </>
            );
            return (
              <article
                key={i}
                className={`border p-6 md:p-8 flex flex-col gap-6 ${
                  b.highlight
                    ? "bg-clay/5 border-clay/40"
                    : "bg-moss/5 border-border"
                }`}
              >
                <div className="w-32 shrink-0 shadow-2xl mx-auto sm:mx-0">
                  <img
                    src={b.cover}
                    alt={b.alt}
                    loading="lazy"
                    decoding="async"
                    width={608}
                    height={896}
                    className="w-full aspect-[2/3] object-cover"
                  />
                </div>
                <div className="flex-1">
                  {b.badge && (
                    <p
                      className={`text-[10px] uppercase tracking-[0.25em] font-bold mb-3 ${
                        b.highlight ? "text-clay" : "text-ink/60"
                      }`}
                    >
                      {b.badge}
                    </p>
                  )}
                  <h3 className="font-serif text-2xl md:text-3xl mb-4 leading-tight text-balance">
                    {b.title}
                  </h3>
                  <p className="text-sm text-ink/70 mb-6 leading-relaxed">{b.body}</p>
                  <a
                    href={b.href}
                    {...(b.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="inline-flex items-center gap-2 group"
                  >
                    {ctaInner}
                  </a>
                  {b.podcast && (
                    <details className="mt-5 group/pod">
                      <summary className="cursor-pointer list-none inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-clay border border-clay/40 hover:bg-clay/10 px-3 py-2 transition-colors">
                        <span aria-hidden>🎧</span>
                        Kitap Özeti Podcast
                        <span className="text-clay/70 group-open/pod:rotate-180 transition-transform">▾</span>
                      </summary>
                      <audio
                        controls
                        preload="none"
                        src={yalnizlikPodcast.url}
                        className="mt-3 w-full"
                      >
                        Tarayıcınız ses oynatmayı desteklemiyor.
                      </audio>
                    </details>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

    </div>
  );
}
